import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { theme } from '../../styles/theme';

const ContactSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background-color: ${theme.colors.gray.light};
`;

const Container = styled.div`
  max-width: ${theme.breakpoints.desktop};
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h2 {
    font-size: 2.5rem;
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.lg};
  }

  p {
    color: ${theme.colors.gray.dark};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  svg {
    width: 24px;
    height: 24px;
    margin-right: ${theme.spacing.md};
    color: ${theme.colors.secondary};
  }

  p {
    margin: 0;
  }
`;

const ContactForm = styled.form`
  background: ${theme.colors.white};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.md};

  label {
    display: block;
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.gray.dark};
    font-weight: 500;
  }

  input,
  textarea {
    width: 100%;
    padding: ${theme.spacing.sm};
    border: 1px solid ${theme.colors.gray.medium};
    border-radius: ${theme.borderRadius.small};
    font-family: inherit;
    font-size: 1rem;
    transition: ${theme.transitions.default};

    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}20;
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    background-color: ${theme.colors.primary};
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: ${theme.colors.gray.medium};
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.primary};
  text-align: center;
  margin-top: ${theme.spacing.md};
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  text-align: center;
  margin-top: ${theme.spacing.md};
  font-weight: 500;
`;

const Contact: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      if (!form.current) return;

      await emailjs.sendForm(
        'service_o2csr1s', // Replace with your Service ID
        'template_9t7bjhf', // Replace with your Template ID
        form.current,
        'Adu7a9Y0DgboDdAYO' // Replace with your Public Key
      );

      setSubmitStatus({
        success: true,
        message: 'Thank you for your message. We will get back to you soon!'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <Grid>
          <ContactInfo>
            <h2>Get in Touch</h2>
            <p>
              Have questions about our products or services? We'd love to hear from you.
              Contact us using the form or through our contact information below.
            </p>
            <InfoItem>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <p>41/A,Jeevanantham street,kollampalayam,erode-638002</p>
            </InfoItem>
            <InfoItem>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              <p>info@jupiternco.com</p>
            </InfoItem>
            <InfoItem>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
              <p>+91 9087188819, +919363618819</p>
            </InfoItem>
          </ContactInfo>
          <ContactForm ref={form} onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </SubmitButton>
            {submitStatus.message && (
              submitStatus.success ? (
                <SuccessMessage>{submitStatus.message}</SuccessMessage>
              ) : (
                <ErrorMessage>{submitStatus.message}</ErrorMessage>
              )
            )}
          </ContactForm>
        </Grid>
      </Container>
    </ContactSection>
  );
};

export default Contact; 